import type { VitalParameterKey } from 'widgets/results/VitalParametersSection';

export type VitalParameterStatus = 'normal' | 'pathological' | 'impossible';

interface VitalParameterBoundary {
  normalMin?: number;
  normalMax?: number;
  impossibleMin: number;
  impossibleMax: number;
  unit: string;
  artikel: string;
  parameterName: string;
}

const BOUNDARIES: Record<VitalParameterKey, VitalParameterBoundary> = {
  systolicMmhg: {
    normalMin: 90,
    normalMax: 129,
    impossibleMin: 40,
    impossibleMax: 350,
    unit: 'mmHg',
    artikel: 'Der',
    parameterName: 'Blutdruck (sys)',
  },
  diastolicMmhg: {
    normalMin: 60,
    normalMax: 89,
    impossibleMin: 20,
    impossibleMax: 200,
    unit: 'mmHg',
    artikel: 'Der',
    parameterName: 'Blutdruck (dia)',
  },
  pulseBpm: {
    normalMin: 60,
    normalMax: 100,
    impossibleMin: 0,
    impossibleMax: 350,
    unit: '/min',
    artikel: 'Der',
    parameterName: 'Puls',
  },
  spo2Percent: {
    normalMin: 95,
    normalMax: 100,
    impossibleMin: 40,
    impossibleMax: 100,
    unit: '%',
    artikel: 'Die',
    parameterName: 'Sauerstoffsättigung',
  },
  temperatureC: {
    normalMin: 36,
    normalMax: 37.9,
    impossibleMin: 24,
    impossibleMax: 43,
    unit: '°C',
    artikel: 'Die',
    parameterName: 'Körpertemperatur',
  },
  respiratoryRateBpm: {
    normalMin: 12,
    normalMax: 20,
    impossibleMin: 0,
    impossibleMax: 80,
    unit: '/min',
    artikel: 'Die',
    parameterName: 'Atemfrequenz',
  },
  weightKg: {
    impossibleMin: 20,
    impossibleMax: 700,
    unit: 'kg',
    artikel: 'Das',
    parameterName: 'Körpergewicht',
  },
  heightCm: {
    normalMin: 148,
    normalMax: 209,
    impossibleMin: 49,
    impossibleMax: 280,
    unit: 'cm',
    artikel: 'Die',
    parameterName: 'Körpergröße',
  },
};

export function getVitalParameterStatus(
  key: VitalParameterKey,
  value: number | null | undefined
): VitalParameterStatus {
  if (value === null || value === undefined) {
    return 'normal';
  }

  const boundary = BOUNDARIES[key];
  if (!boundary) {
    return 'normal';
  }

  if (value < boundary.impossibleMin || value > boundary.impossibleMax) {
    return 'impossible';
  }

  if (
    boundary.normalMin !== undefined &&
    boundary.normalMax !== undefined &&
    (value < boundary.normalMin || value > boundary.normalMax)
  ) {
    return 'pathological';
  }

  return 'normal';
}

export type PathologicalTooltipParams = {
  artikel: string;
  parameterName: string;
  normalMin: number;
  normalMax: number;
  unit: string;
};

export type ImpossibleMessageParams = {
  impossibleMin: number;
  impossibleMax: number;
  unit: string;
};

export function getPathologicalTooltipParams(
  key: VitalParameterKey
): PathologicalTooltipParams | null {
  const boundary = BOUNDARIES[key];
  if (!boundary || boundary.normalMin === undefined || boundary.normalMax === undefined) {
    return null;
  }

  return {
    artikel: boundary.artikel,
    parameterName: boundary.parameterName,
    normalMin: boundary.normalMin,
    normalMax: boundary.normalMax,
    unit: boundary.unit,
  };
}

export function getImpossibleMessageParams(key: VitalParameterKey): ImpossibleMessageParams | null {
  const boundary = BOUNDARIES[key];
  if (!boundary) {
    return null;
  }

  return {
    impossibleMin: boundary.impossibleMin,
    impossibleMax: boundary.impossibleMax,
    unit: boundary.unit,
  };
}
